import React from 'react';
import {
  Box,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  useTheme,
} from '@mui/material';
import { SearchSuggestion } from '../../api/search';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface SearchSuggestionsProps {
  suggestions: SearchSuggestion[];
  loading: boolean;
  query: string;
  visible: boolean;
  onSuggestionClick: (suggestion: SearchSuggestion) => void;
}


const getTypeLabel = (type: string) => {
  switch (type) {
    case 'brand':
      return 'Brand';
    case 'category':
      return 'Category';
    case 'product':
      return 'Product';
    default:
      return 'Product';
  }
};

const highlightText = (text: string, query: string) => {
  if (!query || query.length < 1 || !text) return text;
  
  const regex = new RegExp(`(${query})`, 'gi');
  const parts = text.split(regex);
  
  return parts.map((part, index) => 
    regex.test(part) ? (
      <span key={index} style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>
        {part}
      </span>
    ) : part
  );
};

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({
  suggestions,
  loading,
  query,
  visible,
  onSuggestionClick,
}) => {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  if (!visible || (!loading && suggestions.length === 0)) {
    return null;
  }

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    onSuggestionClick(suggestion);
    
    // Navigate based on suggestion type
    if (suggestion.type === 'brand' && suggestion.id) {
      navigate(`/brand/${suggestion.id}/${encodeURIComponent(suggestion.text)}`);
    } else if (suggestion.type === 'category' && suggestion.id) {
      navigate(`/categories/${suggestion.id}/${encodeURIComponent(suggestion.text)}`);
    } else {
      // For products or when no ID, navigate to search results
      navigate(`/search?q=${encodeURIComponent(suggestion.text)}`);
    }
  };

  const getLocalizedText = (suggestion: SearchSuggestion) => {
    switch (i18n.language) {
      case 'zh':
        return suggestion.textZh || suggestion.text || '';
      case 'cn':
        return suggestion.textCn || suggestion.text || '';
      default:
        return suggestion.text || '';
    }
  };

  return (
    <Box
      sx={{
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: 'white',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        boxShadow: theme.shadows[4],
        zIndex: 1500,
        maxHeight: 400,
        overflow: 'auto',
      }}
    >
      {loading ? (
        <List>
          {Array.from({ length: 4 }).map((_, index) => (
            <ListItem key={index} sx={{ py: 1 }}>
              <ListItemText
                primary={<Skeleton variant="text" width="60%" />}
                secondary={<Skeleton variant="text" width="40%" />}
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <List sx={{ py: 0 }}>
          {suggestions.map((suggestion, index) => (
            <ListItem
              key={`${suggestion.type}-${suggestion.id || index}`}
              component="div"
              onClick={() => handleSuggestionClick(suggestion)}
              sx={{
                py: 1.5,
                px: 2,
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {highlightText(getLocalizedText(suggestion), query)}
                    </Typography>
                    <Chip
                      label={getTypeLabel(suggestion.type)}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.75rem',
                        backgroundColor: theme.palette.grey[100],
                        color: theme.palette.text.secondary,
                      }}
                    />
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                    {i18n.language !== 'en' && suggestion.textZh && (
                      <Typography variant="caption" color="text.secondary">
                        繁: {highlightText(suggestion.textZh, query)}
                      </Typography>
                    )}
                    {i18n.language !== 'cn' && suggestion.textCn && (
                      <Typography variant="caption" color="text.secondary">
                        简: {highlightText(suggestion.textCn, query)}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default SearchSuggestions;
